/**
 * ContingencyHub.js — Phase 3 Contingency Management Hub.
 * Main dashboard showing all contingencies for a buyer's accepted-offer transaction.
 * Tabs: Inspection | Appraisal | Financing | AI Advisor
 */

import React, { useState, useMemo, useCallback } from 'react';
import './ContingencyHub.css';
import { useBuyerTransactions, useTransaction, useContingencyAdvisor } from '../../agents/contingencyAgent';
import InspectionFlow from './InspectionFlow';
import AppraisalFlow from './AppraisalFlow';
import FinancingFlow from './FinancingFlow';
import AIAdvisor from './AIAdvisor';
import ContingencyStateMachinePanel from './ContingencyStateMachinePanel';
import SellerContingencyHub from '../ContingencyAdvisor/SellerContingencyHub';
import ContractHub, { usesContractHub } from './contract/ContractHub';

const CONTINGENCY_TYPE_TABS = {
  inspection: { label: 'Inspection' },
  appraisal:  { label: 'Appraisal' },
  financing:  { label: 'Financing' },
  advisor:    { label: 'AI Advisor' },
};

const CLOSED_STATUSES = ['satisfied', 'removed', 'exercised'];

/**
 * The tab label for one contingency row. A Wisconsin deal has one row per
 * contingency in the signed offer, most of them of type `other`, so the label
 * is the heading the offer prints (`contract.heading`, e.g. "Radon testing")
 * when there is one, else the legacy type label.
 */
export function contingencyTabLabel(contingency) {
  const heading = contingency?.contract?.heading;
  if (heading) return heading;
  const type = contingency?.contingency_type;
  if (CONTINGENCY_TYPE_TABS[type]) return CONTINGENCY_TYPE_TABS[type].label;
  if (typeof type !== 'string' || !type) return 'Contingency';
  return type.replace(/_/g, ' ').replace(/^./, (ch) => ch.toUpperCase());
}

/**
 * The row to select: the one already selected while it still exists, else
 * the first that isn't closed, else the first. Rows are keyed by `id`, never
 * by `contingency_type`, which several WI rows share.
 */
export function pickContingencyId(contingencies, currentId) {
  if (!Array.isArray(contingencies) || contingencies.length === 0) return null;
  if (currentId != null && contingencies.some((c) => c.id === currentId)) return currentId;
  const open = contingencies.find((c) => !CLOSED_STATUSES.includes(c.status));
  return (open || contingencies[0]).id ?? null;
}

const STATUS_NEXT_STEPS = {
  pending: 'Contingency is pending activation. No action needed yet.',
  active: 'Start the contingency workflow now to stay ahead of deadlines.',
  report_uploaded: 'Your report is uploaded. Review extracted details once analysis finishes.',
  analyzing: 'AI is currently processing your report. Refresh shortly for results.',
  analysis_complete: 'Review issues and submit your repair or credit request to the seller.',
  repair_requested: 'Your request has been sent. Wait for seller response or follow up if urgent.',
  repair_responded: 'Seller responded. Review terms and either agree, renegotiate, or exercise.',
  repair_agreed: 'Repairs/credits are agreed. Track completion and mark contingency satisfied.',
  appraisal_entered: 'Appraisal is in. Confirm the value and decide if gap negotiation is needed.',
  gap_resolution_requested: 'Gap resolution sent. Await the seller response.',
  gap_resolution_responded: 'Review seller response and decide whether to proceed.',
  satisfied: 'Contingency is complete.',
  removed: 'Contingency has been removed by agreement.',
  expired: 'Contingency expired. Review available options immediately.',
  exercised: 'Contingency was exercised and the transaction exited this path.',
};

const US_STATE_ABBREVIATIONS = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
  kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD', massachusetts: 'MA',
  michigan: 'MI', minnesota: 'MN', mississippi: 'MS', missouri: 'MO', montana: 'MT',
  nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX',
  utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA', 'west virginia': 'WV',
  wisconsin: 'WI', wyoming: 'WY', 'district of columbia': 'DC',
};

function normalizeStateCode(state) {
  if (!state) return null;
  const value = String(state).trim();
  if (!value) return null;
  if (value.length === 2) return value.toUpperCase();

  const normalized = value.toLowerCase().replace(/\./g, '');
  return US_STATE_ABBREVIATIONS[normalized] || value;
}

function formatTransactionAddress(tx) {
  if (!tx) return null;
  const street = tx.property_address || tx.address || null;
  const city = tx.property_city || tx.city || null;
  const state = normalizeStateCode(tx.property_state || tx.state || null);

  const locality = [city, state].filter(Boolean).join(', ');
  if (street && locality) return `${street}, ${locality}`;
  if (street) return street;
  if (locality) return locality;
  return null;
}

function formatTransactionLocality(tx) {
  if (!tx) return null;
  const city = tx.property_city || tx.city || null;
  const state = normalizeStateCode(tx.property_state || tx.state || null);
  const zip = tx.property_zip_code || tx.zip_code || null;

  const cityState = [city, state].filter(Boolean).join(', ');
  if (cityState && zip) return `${cityState} ${zip}`;
  if (cityState) return cityState;
  return zip || null;
}

function DeadlineBanner({ contingency }) {
  if (!contingency) return null;
  const d = contingency.days_remaining;
  if (d == null) return null;
  const status = contingency.status;
  if (['satisfied', 'removed', 'exercised'].includes(status)) return null;

  let urgency = 'low';
  let text = `${d} days remaining until deadline`;
  if (status === 'expired' || d < 0) { urgency = 'critical'; text = 'Contingency has EXPIRED — take action immediately'; }
  else if (d === 0) { urgency = 'critical'; text = 'Deadline is TODAY — act now'; }
  else if (d <= 1) { urgency = 'critical'; text = `${d} day until deadline — urgent action needed`; }
  else if (d <= 3) { urgency = 'high'; text = `${d} days until deadline`; }
  else if (d <= 7) { urgency = 'medium'; text = `${d} days until deadline`; }
  else { urgency = 'low'; text = `${d} days until deadline`; }

  return (
    <div
      className={`ch-deadline-banner ${urgency}`}
      role={urgency === 'critical' ? 'alert' : 'status'}
    >
      {text}
    </div>
  );
}
function RiskChip({ riskScore }) {
  if (riskScore == null) return null;
  const level = riskScore < 35 ? 'low' : riskScore < 65 ? 'medium' : 'high';
  const label = { low: 'Low Risk', medium: 'Medium Risk', high: 'High Risk' }[level];
  const scoreDisplay = typeof riskScore === 'number' ? riskScore.toFixed(1) : riskScore;
  return (
    <span
      className={`ch-risk-chip ${level}`}
      title={`Risk score: ${scoreDisplay}`}
    >
      {label}
    </span>
  );
}

function NextStepCard({ contingency }) {
  if (!contingency) return null;
  const stepText = STATUS_NEXT_STEPS[contingency.status] || 'Review this contingency and proceed to the next available action.';

  return (
    <div className="ch-next-step-card">
      <div className="ch-next-step-eyebrow">What to do next</div>
      <div className="ch-next-step-text">{stepText}</div>
    </div>
  );
}

function ContingencyTab({ contingency, activeTab, setActiveTab, onRefresh, role }) {
  const { riskScore, loadRiskScore } = useContingencyAdvisor(contingency?.id);

  const stableLoadRiskScore = useCallback(() => {
    if (contingency?.id) loadRiskScore();
  }, [contingency?.id, loadRiskScore]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    stableLoadRiskScore();
  }, [stableLoadRiskScore]);

  if (!contingency) return null;

  return (
    <div>
      <DeadlineBanner contingency={contingency} />

      <NextStepCard contingency={contingency} />

      {/* Risk chip — visible on every tab */}
      {riskScore != null && (
        <div className="ch-risk-chip-wrap">
          <RiskChip riskScore={riskScore} />
        </div>
      )}

      {/* State machine panel */}
      <ContingencyStateMachinePanel contingency={contingency} onRefresh={onRefresh} />

      {/* Contingency-specific tabs */}
      <div className="ch-tabs">
        {Object.entries(CONTINGENCY_TYPE_TABS).map(([key, meta]) => {
          if (key !== 'advisor' && key !== contingency.contingency_type) return null;
          return (
            <button
              key={key}
              className={`ch-tab-btn${activeTab === key ? ' active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {meta.label}
            </button>
          );
        })}
      </div>

      {activeTab === contingency.contingency_type && contingency.contingency_type === 'inspection' && (
        <InspectionFlow contingency={contingency} onRefresh={onRefresh} />
      )}
      {activeTab === contingency.contingency_type && contingency.contingency_type === 'appraisal' && (
        <AppraisalFlow contingency={contingency} onRefresh={onRefresh} />
      )}
      {activeTab === contingency.contingency_type && contingency.contingency_type === 'financing' && (
        <FinancingFlow contingency={contingency} onRefresh={onRefresh} />
      )}
      {activeTab === 'advisor' && (
        <AIAdvisor contingency={contingency} role={role} />
      )}
    </div>
  );
}

function BuyerContingencyHub({ buyerId, focus }) {
  const { transactions, loading: txLoading, error: txError, reload: reloadTxs } = useBuyerTransactions(buyerId);
  // A notification opens the hub on its own transaction and contingency.
  const [selectedTxId, setSelectedTxId] = useState(focus?.transactionId || null);
  // Keyed by contingency id: several WI rows share contingency_type 'other'.
  const [selectedContingencyId, setSelectedContingencyId] = useState(focus?.contingencyId || null);
  const [innerTab, setInnerTab] = useState('inspection');

  // A notification clicked while the hub is already open.
  React.useEffect(() => {
    if (focus?.transactionId) setSelectedTxId(focus.transactionId);
    if (focus?.contingencyId) setSelectedContingencyId(focus.contingencyId);
  }, [focus]);

  const { transaction, loading: detailLoading, error: detailError, reload: reloadTx } = useTransaction(selectedTxId);
  // Wisconsin deals get the contract hub (one card per contingency in the
  // signed offer, written notices); every other state keeps the tabs below.
  const contractMode = usesContractHub(transaction);

  // Auto-select first transaction if only one
  React.useEffect(() => {
    if (!selectedTxId && transactions.length > 0) {
      setSelectedTxId(transactions[0].id);
    }
  }, [transactions, selectedTxId]);

  // Keep the selected contingency across refreshes; otherwise select the
  // first one that isn't closed.
  React.useEffect(() => {
    const rows = transaction?.contingencies;
    if (!rows?.length) return;
    const nextId = pickContingencyId(rows, selectedContingencyId);
    if (nextId === selectedContingencyId) return;
    setSelectedContingencyId(nextId);
    setInnerTab(rows.find((c) => c.id === nextId)?.contingency_type || 'inspection');
  }, [transaction?.id, transaction?.contingencies, selectedContingencyId]);

  const handleRefresh = () => {
    reloadTx();
    reloadTxs();
  };

  // Active contingency (by id)
  const activeContingency = useMemo(() => {
    if (!transaction?.contingencies) return null;
    return transaction.contingencies.find(c => c.id === selectedContingencyId) || null;
  }, [transaction, selectedContingencyId]);

  if (txLoading) return <div className="ch-root"><div className="ch-loading"><div className="ch-spinner" />Loading your transactions…</div></div>;
  if (txError) return <div className="ch-root"><div className="ch-error">{txError}</div></div>;

  if (!transactions.length) {
    return (
      <div className="ch-root">
        <div className="ch-header">
          <h2>Contingency Hub</h2>
          <p>Manage your inspection, appraisal, and financing contingencies</p>
        </div>
        <div className="ch-empty">
          <div className="ch-empty-title">No active transactions</div>
          <div className="ch-empty-body">Once your offer is accepted, your transaction will appear here.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ch-root">
      <div className="ch-header">
        <h2>Contingency Hub</h2>
        <p>Track and manage your transaction contingencies</p>
      </div>

      {/* Transaction selector (multi-transaction support) */}
      {transactions.length > 1 && (
        <div className="ch-tx-selector-wrap">
          <div className="ch-tx-selector-label">Your Transactions</div>
          <div className="ch-tx-select">
            {transactions.map(tx => (
              <button
                key={tx.id}
                type="button"
                className={`ch-tx-card${selectedTxId === tx.id ? ' selected' : ''}`}
                onClick={() => setSelectedTxId(tx.id)}
              >
                <div className="ch-tx-card-body">
                  <div className="ch-tx-card-address">{formatTransactionAddress(tx) || `Transaction #${tx.id}`}</div>
                  <div className="ch-tx-card-meta">
                    {formatTransactionLocality(tx) ? `${formatTransactionLocality(tx)} · ` : ''}
                    Offer #{tx.offer_id} · Status: {tx.status?.replace(/_/g, ' ')}
                  </div>
                </div>
                {tx.health && <span className={`ch-health ${tx.health}`}>{tx.health.replace('_', ' ')}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {!transaction && detailLoading && (
        <div className="ch-loading" role="status"><div className="ch-spinner" />Loading the transaction…</div>
      )}
      {!transaction && detailError && <div className="ch-error" role="alert">{detailError}</div>}

      {/* Transaction overview */}
      {transaction && (
        <>
          <div className="ch-tx-overview-row">
            <div className="ch-tx-overview-info">
              <span className="ch-tx-overview-address">
                {formatTransactionAddress(transaction) || `Transaction #${transaction.id}`}
              </span>
              <span className="ch-tx-overview-meta">
                {formatTransactionLocality(transaction) ? `${formatTransactionLocality(transaction)} · ` : ''}
                Offer #{transaction.offer_id} · Tx #{transaction.id}
              </span>
            </div>
            {transaction.health && (
              <span className={`ch-health ${transaction.health}`}>{transaction.health.replace('_', ' ')}</span>
            )}
          </div>

          {contractMode && (
            // Keyed by transaction: the composer, timeline and refresh state
            // of one deal never carry over to another.
            <ContractHub
              key={transaction.id}
              transaction={transaction}
              role="buyer"
              onRefresh={handleRefresh}
              focusContingencyId={focus?.contingencyId}
            />
          )}

          {/* Contingency type selector (top-level tabs) */}
          {!contractMode && transaction.contingencies?.length > 0 && (
            <div className="ch-tabs ch-tabs--contingency-select">
              {transaction.contingencies.map(c => {
                const urgencyClass = c.days_remaining != null && c.days_remaining <= 3 && !CLOSED_STATUSES.includes(c.status) ? 'high' : '';
                const selected = selectedContingencyId === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={`ch-tab-btn${selected ? ' active' : ''}`}
                    aria-pressed={selected}
                    data-testid={`ch-contingency-tab-${c.id}`}
                    onClick={() => { setSelectedContingencyId(c.id); setInnerTab(c.contingency_type); }}
                  >
                    {contingencyTabLabel(c)}
                    {urgencyClass && <span className={`ch-urgency ${urgencyClass}`}>!</span>}
                    <span className={`ch-status ${c.status}`}>{c.status?.replace(/_/g, ' ')}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Active contingency panel */}
          {contractMode ? null : activeContingency ? (
            <ContingencyTab
              contingency={activeContingency}
              activeTab={innerTab}
              setActiveTab={setInnerTab}
              onRefresh={handleRefresh}
              role="buyer"
            />
          ) : (
            <div className="ch-empty">Select a contingency type above to begin.</div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * @param {{buyerId: (number|string), role?: ('buyer'|'seller'),
 *   focus?: ({transactionId: (number|null), contingencyId: (number|null)}|null)}} props
 *   `focus`: the transaction and contingency a notification opened the hub on.
 */
export default function ContingencyHub({ buyerId, role = 'buyer', focus = null }) {
  if (role === 'seller') {
    return <SellerContingencyHub sellerId={buyerId} focus={focus} />;
  }
  return <BuyerContingencyHub buyerId={buyerId} focus={focus} />;
}
